
/**
 * <div> cannot be a child of <table>
 */
const NON_POPUP_HOST = /^(TABLE|THEAD|TBODY|TFOOT|TR|COLGROUP)$/;

/**
 * The boxes a table clips its rows with, on both axes. rc-trigger reads them as a popup's
 * visible area too (collectScroller/getVisibleArea), so a popup portaled inside one is not
 * just cut off - it is placed within the table's height. See {@link DomUtil.getPopupContainer}.
 */
const TABLE_CLIP = '.ant-table-body, .ant-table-content, .ant-table-tbody-virtual-holder, .ant-table-header';

/** Turns on the `--xm-menu-anim-duration` transitions - see App.less. */
const MENU_ANIM_CLASS = 'xm-menu-anim';

let menuAnimTimeout: number | undefined;

export abstract class DomUtil {

  /**
   * Element ID of main scroll div
   */
  static readonly MAIN_SCROLL_ID = 'main-scroll';

  /**
   * Element ID of the TV mode fullscreen container
   */
  static readonly TV_ROOT_ID = 'xm-tv-root';

  /**
   * How long a menu or panel takes to fold away or back - `--xm-menu-anim-time` from App.less.
   */
  static menuAnimMs(): number {
    const value = getComputedStyle(document.body).getPropertyValue('--xm-menu-anim-time').trim();
    const ms = parseFloat(value) * (value.endsWith('ms') ? 1 : 1000);
    return Number.isFinite(ms) ? ms : 0;
  }

  /**
   * Lets the menus and panels animate close/open. Call right before the state change that folds them.
   */
  static animateMenu(): void {
    document.body.classList.add(MENU_ANIM_CLASS);
    window.clearTimeout(menuAnimTimeout);
    // DomUtil.menuAnimMs() + 100 - the fold starts only once React renders the change
    menuAnimTimeout = window.setTimeout(() => document.body.classList.remove(MENU_ANIM_CLASS), DomUtil.menuAnimMs() + 100);
  }

  /**
     * Waits 1sec for element to appear in specified root DOM
     * @param selector
     * @param root
     * @returns
     */
  static waitForElm(selector, root: HTMLElement) {
    return new Promise<HTMLElement>((resolve, reject) => {
      if (root.querySelector(selector)) {
        return resolve(root.querySelector(selector) as HTMLElement);
      }

      const observer = new MutationObserver(mutations => {
        if (root.querySelector(selector)) {
          resolve(root.querySelector(selector) as HTMLElement);
          observer.disconnect();
        }
      });

      observer.observe(root, {
        childList: true,
        subtree: true
      });

      setTimeout(() => {
        observer.disconnect();
        reject(new Error('Element not found within timeout!'));
      }, 1000);
    });
  }

  /**
   * Gets closest element whose parent is scrollable
   * @param element to start from
   * @returns element or undefined
   */
  static closestScrollable(element: HTMLElement): HTMLElement | undefined {
    const parent = element?.parentElement;
    if (!parent) return;
    const style = getComputedStyle(parent);
    // The shorthand computes to eg. "auto visible" when only overflow-x scrolls,
    // so containers scrolling on either axis count as scrollable.
    const overflow = style.overflow + style.overflowY;
    if (/(auto|scroll)/.test(overflow))
      return element;
    return DomUtil.closestScrollable(parent);
  }

  /**
   * Gets {@link closestScrollable} or undefined if it is table el,
   * so caller can fallback to parent / cell / document.body
   * @param element to start from
   * @returns scrollable element, or undefined
   */
  static closestScrollablePopupHost(element: HTMLElement): HTMLElement | undefined {
    const scrollable = DomUtil.closestScrollable(element);
    if (scrollable && !NON_POPUP_HOST.test(scrollable.tagName))
      return scrollable;
    return undefined;
  }

  /**
   * Root a popup falls back to when nothing closer clips it. `document.body` is outside the
   * TV mode fullscreen element, so a popup portaled there would not render at all.
   * @param triggerNode popup trigger element
   * @returns element to render the popup into
   */
  static popupRoot(triggerNode?: HTMLElement): HTMLElement {
    const fullscreen = document.fullscreenElement as HTMLElement;
    return fullscreen && (!triggerNode || fullscreen.contains(triggerNode)) ? fullscreen : document.body;
  }

  /**
   * `getPopupContainer` opting OUT of {@link getPopupContainer} back to the document root -
   * antd's default, except that {@link popupRoot} keeps a popup inside an open fullscreen element.
   */
  static readonly popupToBody = (triggerNode?: HTMLElement) => DomUtil.popupRoot(triggerNode);

  /**
   * `getPopupContainer` for antd popups: nearest host the popup scrolls and clips with,
   * instead of `document.body`. Reads the DOM only. Class names are antd-version sensitive.
   * @param triggerNode popup trigger element
   * @returns element to render the popup into
   */
  static getPopupContainer(triggerNode: HTMLElement): HTMLElement {
    // Out of the table and into the panel of a modal or drawer (own stacking context),
    // otherwise all the way out to `document.body`. NOT the page scroller (`#main-scroll`):
    // a popup inside a scroller lives in its CONTENT coordinates and so counts towards
    // scrollable overflow, making a scrollbar appear on a page shorter than the viewport,
    // and it would lose the navbar's height of room it needs in a short window.
    if (triggerNode.closest(TABLE_CLIP)) {
      return triggerNode.closest<HTMLElement>('.ant-modal-container')
        ?? triggerNode.closest<HTMLElement>('.ant-drawer-content')
        ?? DomUtil.popupRoot(triggerNode);
    }

    // Keep the popup inside the modal panel (own stacking context).
    const modalPanel = triggerNode.closest<HTMLElement>('.ant-modal-container');
    if (modalPanel && triggerNode.parentElement) {
      return triggerNode.parentElement;
    }

    // Also hosts drawer popups, `.ant-drawer-section` and `.ant-drawer-body` are scrollable.
    const scrollableContainer = DomUtil.closestScrollablePopupHost(triggerNode);
    if (scrollableContainer) {
      return scrollableContainer;
    }

    // A drawer whose body does not scroll: the panel still clips, so stay inside it.
    const drawerPanel = triggerNode.closest<HTMLElement>('.ant-drawer-content');
    if (drawerPanel) {
      return drawerPanel;
    }

    const card = triggerNode.closest<HTMLElement>('.ant-card');
    if (card) {
      return card;
    }

    return triggerNode.parentElement || DomUtil.popupRoot(triggerNode);
  }
}